<template>
  <div v-loading="loading" class="layout mt-4">
    <div
      class="border-gray-200 pb-5 sm:flex sm:items-center sm:justify-between"
      v-if="invoices.length > 0"
    >
      <h3 class="text-lg font-medium leading-6 text-gray-900">
        Unpaid Invoices
      </h3>
      <div class="flex">
        <div class="mt-3 sm:mt-0 sm:ml-4 sm:mr-4">
          <label for="search_invoice" class="sr-only">Search</label>
          <div class="flex rounded-md shadow-sm">
            <div class="relative flex-grow focus-within:z-10">
              <div
                class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"
              >
                <SearchIcon class="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                v-model="search"
                name="search_invoice"
                id="search_invoice"
                class="search block w-full rounded-none rounded-l-md border-gray-300 pl-10 focus:border-indigo-500 sm:hidden 2xl:focus:ring-indigo-500"
                placeholder="Search"
              />
              <input
                type="text"
                name="search_invoice"
                id="search_invoice"
                class="search hidden w-full rounded-none rounded-l-md border-gray-300 pl-10 focus:border-indigo-500 focus:ring-indigo-500 sm:block sm:text-sm"
                placeholder="Search"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="flex flex-col" v-if="invoices.length > 0">
      <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <div class="overflow-hidden border-b border-gray-200 sm:rounded-lg">
            <table class="min-w-full divide-y divide-gray-200 border">
              <thead class="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                  >
                    Amount
                  </th>
                  <th
                    scope="col"
                    class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                  ></th>
                  <th
                    scope="col"
                    class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                  >
                    Customer
                  </th>
                  <th
                    scope="col"
                    class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                  >
                    Number
                  </th>
                  <th
                    scope="col"
                    class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                  >
                    Modified
                  </th>
                  <th
                    scope="col"
                    class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                  >
                    Created
                  </th>
                  <th scope="col" class="px-6 py-3">
                    <span class="sr-only">Edit</span>
                  </th>
                </tr>
              </thead>
              <tbody
                class="divide-y divide-gray-200 bg-white"
                v-if="invoices.length > 0"
              >
                <tr v-for="invoice in invoices" :key="invoice.Id">
                  <td class="whitespace-nowrap px-6 py-4 text-left">
                    <div class="text-sm text-gray-900">
                      ${{ invoice.AmountDue }} USD
                    </div>
                  </td>
                  <td class="whitespace-nowrap px-6 py-4 text-left">
                    <div class="text-sm text-gray-900">
                      <span
                        class="inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-medium"
                        :class="{
                          'bg-green-100 text-green-800': invoice.Status === 2,
                          'bg-red-100 text-red-800': invoice.Status === 3,
                          'bg-yellow-100 text-yellow-800': invoice.Status === 1,
                          'bg-gray-100 text-gray-800': invoice.Status === 0,
                          'bg-red-300 text-red-800': invoice.Status === 4,
                        }"
                      >
                        {{ Status(invoice.Status) }}
                      </span>
                    </div>
                  </td>
                  <td class="whitespace-nowrap px-6 py-4 text-left">
                    <div class="text-sm font-medium text-gray-900">
                      {{ invoice.Customer.Name }}
                    </div>
                    <div class="max-w-sm truncate text-sm text-gray-500">
                      {{ invoice.Customer.Email }}
                    </div>
                  </td>
                  <td class="whitespace-nowrap px-6 py-4 text-left">
                    <span
                      class="inline-flex items-center rounded-md bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-800"
                    >
                      {{ invoice.StripeInvoiceNumber }}
                    </span>
                  </td>
                  <td class="whitespace-nowrap px-6 py-4 text-left">
                    <div class="text-sm text-gray-400">
                      {{
                        new Date(invoice.LastModified).toLocaleDateString(
                          "en-us",
                          {
                            year: "numeric",
                            month: "numeric",
                            day: "numeric",
                          }
                        )
                      }}
                    </div>
                  </td>
                  <td class="whitespace-nowrap px-6 py-4 text-left">
                    <div class="text-sm text-gray-400">
                      {{
                        new Date(invoice.Created).toLocaleDateString("en-us", {
                          year: "numeric",
                          month: "numeric",
                          day: "numeric",
                        })
                      }}
                    </div>
                  </td>
                  <td
                    class="flex-end flex space-x-3 whitespace-nowrap px-6 py-4 text-right text-sm font-medium"
                  >
                    <a
                      :href="invoice.StripeInvoicePDFUrl"
                      v-tippy="{
                        content: 'Download Invoice',
                        animation: 'scale',
                        animateFill: false,
                        theme: 'tomato',
                      }"
                      class="focus:outline-none inline-flex items-center rounded-md border border-transparent border-gray-300 px-3 py-2 text-sm font-medium leading-4 text-purple-400 shadow-sm hover:text-purple-800"
                    >
                      <DownloadIcon
                        class="h-5 w-5 flex-shrink-0 text-gray-400"
                      />
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
            <pagination :pagination="pagination" @changePage="onChangePage" />
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <el-empty
        description="Hooray, no unpaid invoices"
        :image-size="200"
      ></el-empty>
    </div>
  </div>
</template>

<script>
import { onMounted, reactive } from "vue";

import useInvoicesRepository from "@/composable/useInvoicesRepository";
import Pagination from "@/common/components/pagination";

import { SearchIcon, DownloadIcon } from "@heroicons/vue/solid";

import { directive } from "vue-tippy";
import "tippy.js/animations/scale.css";

export default {
  components: {
    Pagination,
    SearchIcon,
    DownloadIcon,
  },
  directives: {
    tippy: directive,
  },
  setup() {
    const { invoices, loading, totalResults, getUnpaidInvoices } =
      useInvoicesRepository();

    const pagination = reactive({
      page: 1,
      pageSize: 10,
      totalResults: totalResults,
    });

    onMounted(() => {
      getUnpaidInvoices(pagination.page, pagination.pageSize);
    });

    const onChangePage = (page) => {
      pagination.page = page;
      getUnpaidInvoices(pagination.page, pagination.pageSize);
    };

    const Status = (status) => {
      switch (status) {
        case 0:
          return "Draft";
        case 1:
          return "Sent";
        case 2:
          return "Paid";
        case 3:
          return "Overdue";
        case 4:
          return "Void";
        default:
          return "Unknown";
      }
    };

    return {
      loading,
      invoices,
      pagination,
      onChangePage,
      Status,
    };
  },
};
</script>

<style scoped>
.layout {
  overflow: hidden;
}
</style>
